import type {
    Column,
    ColumnOrderState,
    ColumnPinningState,
    ColumnSizingState,
    RowData,
    Table as TanStackTable,
    VisibilityState
} from "@tanstack/react-table";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {
    COMPACT_TABLE_WIDTH,
    MAX_COLUMN_SIZE,
    MIN_COLUMN_SIZE,
    PINNED_COLUMN_MAX_FRACTION
} from "./constants";

export const getVisibleColumns = <TData extends RowData>(table: TanStackTable<TData>) => [
    ...table.getLeftVisibleLeafColumns(),
    ...table.getCenterVisibleLeafColumns(),
    ...table.getRightVisibleLeafColumns()
];

const COLUMN_PREFERENCES_VERSION = 1;
const columnPreferencesStorageKey = (tableId: string) => `server-table-preferences:${tableId}`;
const legacySizingStorageKey = (tableId: string) => `server-table-sizing:${tableId}`;

export interface ServerTableColumnPreferences {
    columnVisibility: VisibilityState
    columnPinning: ColumnPinningState
    columnOrder: ColumnOrderState
    columnSizing: ColumnSizingState
}

interface PersistedServerTableColumnPreferences extends ServerTableColumnPreferences {
    version: number
}

const sanitizeColumnIds = (value: unknown, knownIds: Set<string>) => {
    if (!Array.isArray(value)) return [];

    return Array.from(new Set(value.filter((id): id is string => (
        typeof id === 'string' && knownIds.has(id)
    ))));
};

/*
    A column that arrives after a user stored their preferences slots in next to the column it is
    defined after, not at the far end of the table where nobody looks for it. The stored order still
    wins for every column the user has already seen.
*/
const withNewColumnsInPlace = (storedOrder: ColumnOrderState, defaultOrder: ColumnOrderState): ColumnOrderState => {
    const merged = [...storedOrder];
    defaultOrder.forEach((columnId, index) => {
        if (merged.includes(columnId)) return;
        const predecessor = defaultOrder.slice(0, index).reverse().find(id => merged.includes(id));
        merged.splice(predecessor ? merged.indexOf(predecessor) + 1 : 0, 0, columnId);
    });
    return merged;
};

export const normalizeServerTableColumnPreferences = (
    stored: Partial<ServerTableColumnPreferences>,
    defaults: ServerTableColumnPreferences
): ServerTableColumnPreferences => {
    const knownIds = new Set(defaults.columnOrder);
    const storedVisibility = stored.columnVisibility ?? {};
    const columnVisibility = Object.fromEntries(defaults.columnOrder.map(columnId => [
        columnId,
        typeof storedVisibility[columnId] === 'boolean'
            ? storedVisibility[columnId]
            : defaults.columnVisibility[columnId] ?? true
    ]));
    const storedOrder = sanitizeColumnIds(stored.columnOrder, knownIds);
    const columnOrder = withNewColumnsInPlace(storedOrder, defaults.columnOrder);
    const storedLeft = sanitizeColumnIds(stored.columnPinning?.left, knownIds)
        .filter(columnId => columnVisibility[columnId] !== false);
    const storedLeftIds = new Set(storedLeft);
    const storedRight = sanitizeColumnIds(stored.columnPinning?.right, knownIds)
        .filter(columnId => columnVisibility[columnId] !== false && !storedLeftIds.has(columnId));
    const hasStoredPinning = stored.columnPinning !== undefined;
    const columnPinning = hasStoredPinning
        ? {left: storedLeft, right: storedRight}
        : {
            left: sanitizeColumnIds(defaults.columnPinning.left, knownIds)
                .filter(columnId => columnVisibility[columnId] !== false),
            right: sanitizeColumnIds(defaults.columnPinning.right, knownIds)
                .filter(columnId => columnVisibility[columnId] !== false)
        };
    const columnSizing = Object.fromEntries(Object.entries(stored.columnSizing ?? {})
        .filter(([columnId, size]) => (
            knownIds.has(columnId)
            && typeof size === 'number'
            && size >= MIN_COLUMN_SIZE
            && size <= MAX_COLUMN_SIZE
        ))) as ColumnSizingState;

    return {columnVisibility, columnPinning, columnOrder, columnSizing};
};

export const getStoredServerTableColumnPreferences = (
    tableId: string,
    defaults: ServerTableColumnPreferences
) => {
    try {
        const raw = window.localStorage.getItem(columnPreferencesStorageKey(tableId));
        if (raw) {
            const parsed = JSON.parse(raw) as Partial<PersistedServerTableColumnPreferences>;
            if (parsed.version === COLUMN_PREFERENCES_VERSION) {
                return normalizeServerTableColumnPreferences(parsed, defaults);
            }
        }

        const legacySizing = window.localStorage.getItem(legacySizingStorageKey(tableId));
        return normalizeServerTableColumnPreferences({
            columnSizing: legacySizing ? JSON.parse(legacySizing) : {}
        }, defaults);
    } catch {
        return defaults;
    }
};

export const storeServerTableColumnPreferences = (
    tableId: string,
    preferences: ServerTableColumnPreferences
) => {
    try {
        window.localStorage.setItem(columnPreferencesStorageKey(tableId), JSON.stringify({
            version: COLUMN_PREFERENCES_VERSION,
            ...preferences
        }));
        window.localStorage.removeItem(legacySizingStorageKey(tableId));
    } catch {
        // The table still works when browser storage is unavailable.
    }
};

export const getPinnedWidthCap = (containerWidth: number) =>
    containerWidth > 0 && containerWidth < COMPACT_TABLE_WIDTH
        ? Math.round(containerWidth * PINNED_COLUMN_MAX_FRACTION)
        : null;

export const getEffectiveColumnWidths = <TData extends RowData>(
    table: TanStackTable<TData>,
    containerWidth: number
) => {
    const visibleColumns = getVisibleColumns(table);
    const widths = Object.fromEntries(visibleColumns.map(column => [column.id, column.getSize()]));
    const pinnedColumns = table.getLeftVisibleLeafColumns();
    const pinnedWidthCap = getPinnedWidthCap(containerWidth);
    const pinnedWidth = pinnedColumns.reduce((sum, column) => sum + column.getSize(), 0);

    if (pinnedWidthCap === null || pinnedWidth <= pinnedWidthCap) return widths;

    let assignedWidth = 0;
    pinnedColumns.forEach((column, index) => {
        const width = index === pinnedColumns.length - 1
            ? pinnedWidthCap - assignedWidth
            : column.getSize() * pinnedWidthCap / pinnedWidth;
        widths[column.id] = width;
        assignedWidth += width;
    });

    return widths;
};

export const getEffectiveColumnWidth = <TData extends RowData>(
    column: Column<TData, unknown>,
    effectiveWidths: Record<string, number>
) => effectiveWidths[column.id] ?? column.getSize();

export const getColumnAlignment = <TData extends RowData>(column: Column<TData, unknown>) =>
    column.columnDef.meta?.align ?? 'left';

// gets the styles for pinned columns and keeps the final pinned divider visible
export const getPinnedColumnSx = <TData extends RowData>(column: Column<TData, unknown>, zIndex: number) => {
    if (column.getIsPinned() !== 'left') {
        return {};
    }

    const isLastPinnedColumn = column.getIsLastColumn('left');

    return {
        position: 'sticky' as const,
        left: `var(--server-table-pinned-left-${column.getPinnedIndex()}, ${column.getStart('left')}px)`,
        zIndex,
        backgroundColor: 'background.paper',
        boxShadow: isLastPinnedColumn
            ? '8px 0 16px -16px rgba(15, 23, 42, 0.45)'
            : undefined,
        ...(isLastPinnedColumn ? {
            '&::after': {
                content: '""',
                position: 'absolute' as const,
                top: 0,
                right: 0,
                bottom: 0,
                width: '1px',
                backgroundColor: CommonThemeColors.ref.neutral.neutral80,
                pointerEvents: 'none' as const
            }
        } : {})
    };
};
